import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const categories = [
  {
    label: 'Frontend',
    accent: '#3b82f6',
    skills: ['React', 'JavaScript (ES6+)', 'HTML5', 'CSS3', 'Tailwind CSS'],
  },
  {
    label: 'Backend',
    accent: '#8b5cf6',
    skills: ['Node.js', 'Express.js', 'REST APIs'],
  },
  {
    label: 'Database',
    accent: '#22d3ee',
    skills: ['MongoDB', 'Mongoose'],
  },
  {
    label: 'Tools & Design',
    accent: '#ec4899',
    skills: ['Figma', 'Git', 'GitHub', 'Vercel', 'WordPress'],
  },
];

const highlights = [
  { label: 'Core Stack', value: 'MERN' },
  { label: 'Design Tool', value: 'Figma' },
  { label: 'Deploy', value: 'Vercel' },
  { label: 'Version Control', value: 'Git / GitHub' },
];

export default function Skills() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="skills" style={{ padding: '120px 0' }}>
      <div className="section-container">
        <motion.p ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }} className="section-label">
          Skills
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.1 }} className="section-title" style={{ marginBottom: '0.75rem' }}>
          Tech{' '}
          <span style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Stack
          </span>
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '4rem', maxWidth: '420px', lineHeight: 1.7 }}>
          Technologies I use to build fast, scalable, full-stack web applications.
        </motion.p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem', marginBottom: '3rem' }}>
          {categories.map((cat, i) => (
            <motion.div key={cat.label} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.25 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="glass-card" style={{ padding: '1.5rem' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1.1rem' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: cat.accent, boxShadow: `0 0 10px ${cat.accent}80` }} />
                <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#f0f6ff' }}>
                  {cat.label}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                {cat.skills.map((skill, si) => (
                  <motion.div key={skill} initial={{ opacity: 0, x: -10 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.4 + i * 0.1 + si * 0.05 }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <span style={{ fontSize: '0.6rem', color: cat.accent }}>▹</span>
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>{skill}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.6 }}
          style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '18px', padding: '1.75rem 2rem' }}>
          <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1.25rem' }}>
            Quick Reference
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
            {highlights.map(h => (
              <div key={h.label}>
                <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>{h.label}</p>
                <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.95rem', fontWeight: 600, color: '#f0f6ff' }}>{h.value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
